import { useState, useEffect } from 'react'
import { useUpdateGameAsAdmin } from '@/hooks/useGames'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { EditGoalModal } from './EditGoalModal'
import { X, Save, Trash2, Plus, Edit2, Clock } from 'lucide-react'

interface GameParticipant {
  id: string
  user_id: string
  team: 'red' | 'blue'
  role: 'defense' | 'attack' | 'full'
  user: {
    id: string
    display_name: string
  }
}

interface Goal {
  id: string
  scored_by_team: 'red' | 'blue'
  goal_type: string
  shooter_role: 'attack' | 'midfield' | 'defense' | 'goalkeeper'
  points_awarded: number
  touched_by_defender: boolean
  goal_time: string | null
  created_at: string
  notes?: string
  user: {
    id: string
    display_name: string
    profile_picture_url?: string
  } | null
}

interface Game {
  id: string
  date: string
  game_type: '1v1' | '2v2'
  final_red_score: number
  final_blue_score: number
  winner?: 'red' | 'blue'
  participants: GameParticipant[]
  goals?: Goal[]
}

interface EditGameModalProps {
  game: Game
  isOpen: boolean
  onClose: () => void
}

export function EditGameModal({ game, isOpen, onClose }: EditGameModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [activeTab, setActiveTab] = useState<'scores' | 'goals'>('scores')
  const [redScore, setRedScore] = useState(game.final_red_score)
  const [blueScore, setBlueScore] = useState(game.final_blue_score)
  const [goals, setGoals] = useState<Goal[]>([])
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)
  const [showAddGoal, setShowAddGoal] = useState(false)
  const updateGame = useUpdateGameAsAdmin()

  // Fetch game with goals
  const { data: gameWithGoals, isLoading } = useQuery({
    queryKey: ['game', game.id],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/games/${game.id}`)
      return response.data
    },
    enabled: isOpen && !!game.id,
  })

  useEffect(() => {
    if (isOpen) {
      setRedScore(game.final_red_score)
      setBlueScore(game.final_blue_score)
    }
  }, [isOpen, game])

  useEffect(() => {
    if (gameWithGoals?.goals) {
      setGoals(gameWithGoals.goals)
    }
  }, [gameWithGoals])

  const handleSave = async () => {
    try {
      await updateGame.mutateAsync({
        gameId: game.id,
        gameData: {
          final_red_score: parseInt(redScore.toString()),
          final_blue_score: parseInt(blueScore.toString()),
          goals: goals.length > 0 ? goals.map(goal => ({
            scored_by_team: goal.scored_by_team,
            shooter_role: goal.shooter_role,
            goal_type: goal.goal_type,
            touched_by_defender: goal.touched_by_defender,
            notes: goal.notes || '',
            goal_time: goal.goal_time
          })) : undefined
        }
      })
      onClose()
    } catch (error: any) {
      console.error('Failed to update game:', error)
      console.error('Error details:', error.response?.data || error.message)
      console.error('Full error response:', error.response)
      console.error('Request data being sent:', {
        gameId: game.id,
        gameData: {
          final_red_score: redScore,
          final_blue_score: blueScore,
          goals: goals.map(goal => ({
            scored_by_team: goal.scored_by_team,
            shooter_role: goal.shooter_role,
            goal_type: goal.goal_type,
            touched_by_defender: goal.touched_by_defender,
            notes: goal.notes,
            goal_time: goal.goal_time
          }))
        }
      })
      alert(`Failed to update game: ${error.response?.data?.message || error.message || 'Unknown error'}`)
    }
  }

  const getParticipantsByTeam = (participants: GameParticipant[], team: 'red' | 'blue') => {
    return participants
      .filter(p => p.team === team)
      .map(p => p.user.display_name)
      .join(', ')
  }

  const getGoalTypeLabel = (goalType: string) => {
    const labels: Record<string, string> = {
      'attack_shot': 'Attack Shot',
      'midfield_shot': 'Midfield Shot',
      'defense_shot': 'Defense Shot',
      'goalkeeper_shot': 'Goalkeeper Shot',
      'own_goal_attack': 'Own Goal (Attack)',
      'own_goal_midfield': 'Own Goal (Midfield)',
      'own_goal_defense': 'Own Goal (Defense)',
      'own_goal_goalkeeper': 'Own Goal (Goalkeeper)',
      'gamelle': 'Gamelle',
      'gamelle_attack': 'Gamelle (Attack)',
      'gamelle_midfield': 'Gamelle (Midfield)',
      'gamelle_defense': 'Gamelle (Defense)',
      'gamelle_goalkeeper': 'Gamelle (Goalkeeper)',
      'monsterblock_defense': 'Monsterblock (Defense)',
      'monsterblock_goalkeeper': 'Monsterblock (Goalkeeper)'
    }
    return labels[goalType] || goalType
  }

  const formatTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const addGoal = () => {
    const newGoal: Goal = {
      id: `temp-${Date.now()}`,
      scored_by_team: 'red',
      goal_type: 'attack_shot',
      shooter_role: 'attack',
      points_awarded: 2,
      touched_by_defender: false,
      goal_time: new Date().toISOString(),
      created_at: new Date().toISOString(),
      notes: '',
      user: null
    }
    setGoals([...goals, newGoal])
    setEditingGoal(newGoal)
    setShowAddGoal(false)
  }

  const updateGoal = (updatedGoal: Goal) => {
    setGoals(goals.map(g => g.id === updatedGoal.id ? updatedGoal : g))
    setEditingGoal(null)
  }

  const deleteGoal = (goalId: string) => {
    setGoals(goals.filter(g => g.id !== goalId))
  }

  if (!isOpen) return null

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading game details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">Edit Game</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('scores')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'scores'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Scores
            </button>
            <button
              onClick={() => setActiveTab('goals')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'goals'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Goals ({goals.length})
            </button>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'scores' ? (
            <div className="space-y-4">
              {/* Game Info */}
              <div className="text-sm text-gray-600">
                <p><strong>Date:</strong> {new Date(game.date).toLocaleDateString()}</p>
                <p><strong>Type:</strong> {game.game_type === '1v1' ? '1v1' : '2v2'}</p>
              </div>

              {/* Teams */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-red-900">Red Team</p>
                    <p className="text-sm text-red-700">{getParticipantsByTeam(game.participants, 'red')}</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={redScore}
                    onChange={(e) => setRedScore(parseInt(e.target.value) || 0)}
                    className="w-16 px-2 py-1 border border-red-300 rounded text-center font-medium text-red-900"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-900">Blue Team</p>
                    <p className="text-sm text-blue-700">{getParticipantsByTeam(game.participants, 'blue')}</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={blueScore}
                    onChange={(e) => setBlueScore(parseInt(e.target.value) || 0)}
                    className="w-16 px-2 py-1 border border-blue-300 rounded text-center font-medium text-blue-900"
                  />
                </div>
              </div>

              {/* Winner Display */}
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Winner: {redScore > blueScore ? 'Red Team' : blueScore > redScore ? 'Blue Team' : 'Tie'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-medium text-gray-900">Goals</h4>
                <button
                  onClick={addGoal}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Goal
                </button>
              </div>

              {goals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No goals recorded</p>
                  <p className="text-sm">Click "Add Goal" to start recording goals</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {goals.map((goal, index) => (
                    <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              goal.scored_by_team === 'red' 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {goal.scored_by_team.toUpperCase()}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {getGoalTypeLabel(goal.goal_type)}
                            </span>
                            <span className="text-sm text-gray-500">
                              ({goal.shooter_role})
                            </span>
                            {goal.touched_by_defender && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                Touched
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>+{goal.points_awarded} points</span>
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatTime(goal.goal_time)}
                            </span>
                            {goal.notes && (
                              <span className="italic">"{goal.notes}"</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setEditingGoal(goal)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Edit goal"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteGoal(goal.id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Delete goal"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={updateGame.isPending}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {updateGame.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Edit Goal Modal */}
      <EditGoalModal
        goal={editingGoal}
        isOpen={!!editingGoal}
        onClose={() => setEditingGoal(null)}
        onSave={updateGoal}
      />
    </div>
  )
}
